import api from './api';
import { uid } from '../utils/mockData';

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false';

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * In mock mode we reflect the onboarding flag from the localStorage mock
 * so returning users who've already saved links skip the setup screen.
 */
function mockHasSocialLinks() {
  try {
    const raw = localStorage.getItem('sp_mock_social_links');
    if (!raw) return false;
    const p = JSON.parse(raw);
    return Boolean(p?.facebook || p?.instagram || p?.linkedin || p?.tiktok);
  } catch {
    return false;
  }
}

export const authService = {
  async login({ email, password }) {
    if (USE_MOCK) {
      await delay(500);
      if (!email || !password) {
        throw Object.assign(new Error('Missing credentials'), {
          response: { status: 400, data: { message: 'Email & password required' } },
        });
      }
      return {
        token: `mock.${uid('tok')}`,
        user: {
          id: uid('u'),
          email,
          name: email.split('@')[0],
          role: 'user',
          has_social_links: mockHasSocialLinks(),
        },
      };
    }
    const { data } = await api.post('/auth/login', { email, password });
    return data;
  },

  async register({ name, email, password }) {
    if (USE_MOCK) {
      await delay(600);
      if (!email || !password) {
        throw Object.assign(new Error('Missing fields'), {
          response: { status: 400, data: { message: 'Missing required fields' } },
        });
      }
      return {
        token: `mock.${uid('tok')}`,
        user: {
          id: uid('u'),
          email,
          name: name || email.split('@')[0],
          role: 'user',
          // New mock users always start without links so they hit onboarding.
          has_social_links: false,
        },
      };
    }
    const { data } = await api.post('/auth/register', { name, email, password });
    return data;
  },

  async logout() {
    if (USE_MOCK) {
      await delay(200);
      return { success: true };
    }
    const { data } = await api.post('/auth/logout');
    return data;
  },

  async me() {
    if (USE_MOCK) {
      await delay(200);
      return null;
    }
    const { data } = await api.get('/auth/me');
    return data;
  },

  async forgotPassword({ email }) {
    if (USE_MOCK) {
      await delay(450);
      if (!email) {
        throw Object.assign(new Error('Missing email'), {
          response: { status: 400, data: { message: 'Email is required' } },
        });
      }
      return { success: true, message: 'Reset link sent' };
    }
    const { data } = await api.post('/auth/forgot-password', { email });
    return data;
  },

  /**
   * Update the current user's profile.
   * Backend contract: PATCH /auth/me with optional { name, email, avatar }.
   * Avatar is sent as a data URL string; in production you'd typically
   * upload to a dedicated endpoint and store the returned URL instead.
   */
  async updateProfile(patch) {
    if (USE_MOCK) {
      await delay(500);
      // In mock mode we just echo the patch back; the store merges it
      // into the existing user object.
      return { ...patch };
    }
    const { data } = await api.patch('/auth/me', patch);
    return data;
  },
};
